import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { marketingAction } from '../../_actions/marketing.actions';

/**
 * Salon-controlled marketing (Charlie, 27 Sep): when a self-employed stylist
 * and their salon have both switched it on, the SALON runs the stylist's
 * marketing. The stylist cannot create, send or schedule campaigns or
 * journeys, and does not see or spend the salon's credits. The server
 * enforces all of that; these screens only say so plainly.
 *
 * The settings read (setting/get) carries `marketingManagedBySalon` and
 * `managedBySalonName` for a self-employed caller. Anyone else gets neither,
 * so this reads as "not managed" and nothing changes for them.
 */
export function readMarketingControl(result) {
  const r = result || {};
  return {
    managed: r.marketingManagedBySalon === true,
    salonName: r.managedBySalonName || '',
  };
}

export function useMarketingControl() {
  const dispatch = useDispatch();
  const [state, setState] = useState({ managed: false, salonName: '', loaded: false });

  useEffect(() => {
    let alive = true;
    dispatch(marketingAction.getSettings())
      .then((res) => {
        if (!alive) return;
        const r = res && res.data && res.data.responseData && res.data.responseData.result;
        setState({ ...readMarketingControl(r), loaded: true });
      })
      .catch(() => { if (alive) setState((s) => ({ ...s, loaded: true })); });
    return () => { alive = false; };
  }, [dispatch]);

  return state;
}

/** The one sentence a stylist under salon control needs, with the way out. */
export function SalonRunsMarketingBanner({ salonName, style }) {
  const history = useHistory();
  const who = salonName || 'Your salon';
  return (
    <div
      role="status"
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px',
        background: '#F2FBFD', border: '1px solid #B9DDE6', borderRadius: 12,
        margin: '0 0 18px', ...(style || {}),
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,126,160,0.12)', color: '#007EA0',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" /><path d="M12 8h.01" /><path d="M11 12h1v4h1" />
        </svg>
      </span>
      <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: '#1A1A1E' }}>
        <strong>{who} runs your marketing.</strong> Your clients get the salon&rsquo;s campaigns.
        {' '}You can change this in{' '}
        <button
          type="button"
          onClick={() => history.push('/marketing-setting')}
          style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', fontWeight: 600, color: '#007EA0', cursor: 'pointer', textDecoration: 'underline' }}
        >
          Config
        </button>
        .
      </p>
    </div>
  );
}

/** Label for a draft or schedule the server is holding back. */
export const HELD_REASON = 'salon_runs_marketing';
export const isHeld = (row) => !!row && (row.heldReason === HELD_REASON || (row._raw && row._raw.heldReason === HELD_REASON));
